/**
 * Provider-neutral softphone facade — the ONLY device surface
 * `use-voice-session` talks to. Picks the concrete implementation from the
 * minted token's `provider` discriminator at init time:
 *
 *  - twilio → the battle-tested module `voice-device.ts` (kept byte-identical;
 *    its Call-object handlers are adapted to the neutral events here)
 *  - telnyx → `telnyx-voice-device.ts` (@telnyx/webrtc)
 *
 * The provider cannot change mid-session (switching providers re-provisions
 * voice and re-mints), so it is resolved once per init and re-resolved on the
 * next init after destroy.
 */
import axios from 'axios';

import { voiceApi } from '@/api/services/voice.api';
import type { IVoiceDeviceHandlers } from './types';
import { installWebrtcDiagnostics } from './webrtc-diagnostics';
import {
    acceptPending as twilioAcceptPending,
    connectOutboundLeg as twilioConnectOutboundLeg,
    destroyVoiceDevice as twilioDestroy,
    getActiveCall as twilioGetActiveCall,
    hangupActive as twilioHangupActive,
    initVoiceDevice as twilioInit,
    listPendingCallIds as twilioListPendingCallIds,
    muteActive as twilioMuteActive,
    recoveryDelayMs,
    rejectPending as twilioRejectPending,
    resolveCallId as twilioResolveCallId,
    sendDigits as twilioSendDigits,
} from './voice-device';
import {
    cancelOutboundExpectation as telnyxCancelOutboundExpectation,
    destroyTelnyxDevice,
    expectAndAnswerOutboundLeg,
    initTelnyxDevice,
    telnyxAcceptPending,
    telnyxGetActiveCallId,
    telnyxHangupActive,
    telnyxHangupLeg,
    telnyxIsActiveMuted,
    telnyxListPendingCallIds,
    telnyxRejectPending,
    telnyxSendDigits,
    telnyxSetMuted,
} from './telnyx-voice-device';

let provider: 'twilio' | 'telnyx' = 'twilio';

// The mint below happens BEFORE either device module records its handlers, so
// neither module's own backoff can cover a mint failure — their
// `scheduleRecovery()` guards early-return on a null handler. The facade owns
// that window itself; without this a backend hiccup (or a misconfigured org)
// pinned the widget at "offline" with nothing retrying until a page reload.
let sessionHandlers: IVoiceDeviceHandlers | null = null;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryAttempt = 0;

function cancelRecovery(): void {
    if (recoveryTimer) {
        clearTimeout(recoveryTimer);
        recoveryTimer = null;
    }
    recoveryAttempt = 0;
}

/**
 * A mint refused with a 4xx names a configuration problem no retry can fix —
 * voice not enabled for the org, or provisioned against a provider it no longer
 * uses. The ordinary backoff caps at 30s and then repeats FOREVER: production
 * Sentry breadcrumbs (15 Sep) carried ~140 `POST /voice/token -> 409` in a single
 * hour from one tab, invisible because a 409 is not an exception.
 *
 * Probing slowly rather than stopping is deliberate. `canUseVoice` is a
 * permission check, so it does not change when an admin fixes provider config —
 * stopping outright would leave every open tab offline until a page reload. This
 * keeps that self-heal at 1/20th of the traffic.
 *
 * 408 and 429 are excluded: a timeout or a rate limit IS transient and must keep
 * the fast backoff.
 */
const PERMANENT_MINT_RETRY_MS = 10 * 60_000;

function isPermanentMintFailure(error: unknown): boolean {
    if (!axios.isAxiosError(error)) return false;
    const status = error.response?.status;
    if (status === undefined || status === 408 || status === 429) return false;
    return status >= 400 && status < 500;
}

/**
 * One pending attempt at a time; each failure schedules the next. `delayMs`
 * overrides the backoff for a failure known to be permanent, and deliberately
 * does NOT advance `recoveryAttempt` so the transient ladder is unchanged.
 */
function scheduleRecovery(delayMs?: number): void {
    if (recoveryTimer || !sessionHandlers) return;
    const delay = delayMs ?? recoveryDelayMs(recoveryAttempt);
    if (delayMs === undefined) recoveryAttempt += 1;
    recoveryTimer = setTimeout(() => {
        recoveryTimer = null;
        const handlers = sessionHandlers;
        if (!handlers) return; // destroyed while waiting
        void initSessionDevice(handlers).catch(() => {
            // initSessionDevice already scheduled the next attempt.
        });
    }, delay);
}

export async function initSessionDevice(handlers: IVoiceDeviceHandlers): Promise<void> {
    installWebrtcDiagnostics(); // dev-only media tap; no-op in production builds
    sessionHandlers = handlers;

    let minted;
    try {
        minted = await voiceApi.mintToken();
    } catch (error: unknown) {
        scheduleRecovery(isPermanentMintFailure(error) ? PERMANENT_MINT_RETRY_MS : undefined);
        throw error;
    }
    provider = minted.provider ?? 'twilio';
    console.warn(`[voice-diag] session device init — provider: ${provider}`);

    try {
        if (provider === 'telnyx') {
            await initTelnyxDevice(minted, handlers);
        } else {
            // Adapt the Twilio module's Call-object callbacks to the neutral
            // events. `minted` is threaded through so the Twilio module reuses
            // this token instead of minting a second one per init.
            await twilioInit(minted, {
                onRegistered: handlers.onRegistered,
                onUnregistered: handlers.onUnregistered,
                onError: handlers.onError,
                onIncoming: (call) => {
                    const callId = twilioResolveCallId(call);
                    if (!callId) return;
                    call.on('cancel', () => handlers.onCallEvent(callId, { type: 'cancel' }));
                    call.on('reject', () => handlers.onCallEvent(callId, { type: 'reject' }));
                    call.on('disconnect', () =>
                        handlers.onCallEvent(callId, { type: 'disconnect' }),
                    );
                    call.on('accept', () => handlers.onCallEvent(callId, { type: 'accept' }));
                    call.on('mute', (muted: boolean) =>
                        handlers.onCallEvent(callId, { type: 'mute', muted }),
                    );
                    handlers.onIncoming({ callId });
                },
            });
        }
    } catch (error: unknown) {
        // Both modules arm their own retry once they hold the handlers; this
        // covers the window where they threw before doing so.
        scheduleRecovery();
        throw error;
    }
    cancelRecovery();
}

export function destroySessionDevice(): void {
    // Cleared before teardown so a module's own 'unregistered'/'socket.close'
    // event can't schedule a recovery for a device we are disposing of.
    sessionHandlers = null;
    cancelRecovery();
    if (provider === 'telnyx') destroyTelnyxDevice();
    else twilioDestroy();
    provider = 'twilio';
}

/**
 * Originate the browser leg for an already-created Call row. Twilio dials the
 * org's TwiML App; Telnyx auto-answers the leg the backend dialed to this
 * agent's SIP credential.
 */
export async function connectOutboundLeg(callId: string): Promise<void> {
    if (provider === 'telnyx') {
        expectAndAnswerOutboundLeg(callId);
        return;
    }
    await twilioConnectOutboundLeg(callId);
}

/**
 * The outbound call ended (any reason) — retire its auto-answer expectation so
 * a stale one can't swallow the next inbound agent leg. Twilio dials its own
 * leg, so there is nothing to cancel there.
 */
export function cancelOutboundExpectation(callId: string): void {
    if (provider === 'telnyx') telnyxCancelOutboundExpectation(callId);
}

export function acceptPending(callId: string): boolean {
    return provider === 'telnyx' ? telnyxAcceptPending(callId) : twilioAcceptPending(callId);
}

export function rejectPending(callId: string): void {
    if (provider === 'telnyx') telnyxRejectPending(callId);
    else twilioRejectPending(callId);
}

export function listPendingCallIds(): string[] {
    return provider === 'telnyx' ? telnyxListPendingCallIds() : twilioListPendingCallIds();
}

export function getActiveCallId(): string | null {
    if (provider === 'telnyx') return telnyxGetActiveCallId();
    const call = twilioGetActiveCall();
    return call ? twilioResolveCallId(call) : null;
}

export function hasActiveCall(): boolean {
    return provider === 'telnyx' ? telnyxGetActiveCallId() !== null : !!twilioGetActiveCall();
}

export function hangupActive(): void {
    if (provider === 'telnyx') telnyxHangupActive();
    else twilioHangupActive();
}

/**
 * The backend declared this call over (WS `ended`) — defensively tear down
 * any local SDK leg still alive for it. Twilio legs are hung up server-side
 * by the backend, so only a matching active connection needs the nudge;
 * Telnyx legs are bare Call Control dials the backend loses track of once
 * the Call row is terminal, so the browser must hang them up itself.
 */
export function endLocalLeg(callId: string): void {
    if (provider === 'telnyx') {
        telnyxHangupLeg(callId);
        return;
    }
    const call = twilioGetActiveCall();
    if (call && twilioResolveCallId(call) === callId) twilioHangupActive();
}

/** True when the underlying SDK actually acted; false = no leg to mute. */
export function setMuted(muted: boolean): boolean {
    if (provider === 'telnyx') return telnyxSetMuted(muted);
    return twilioMuteActive(muted);
}

export function isActiveMuted(): boolean {
    if (provider === 'telnyx') return telnyxIsActiveMuted();
    return twilioGetActiveCall()?.isMuted() ?? false;
}

export function sendDigits(digits: string): void {
    if (provider === 'telnyx') telnyxSendDigits(digits);
    else twilioSendDigits(digits);
}
